<template>
  <div
    :class="[
      'kpi-secondary',
      {
        'kpi-secondary--danger': danger && value > 0,
        'kpi-secondary--clickable': clickable
      }
    ]"
    @click="onClick"
  >
    <div class="kpi-sec-icon" :style="{ background: iconBg }">
      <i :class="icon" :style="{ color: iconColor }"></i>
    </div>
    <div class="kpi-sec-content">
      <div class="kpi-sec-label-row">
        <span class="kpi-sec-label">{{ label }}</span>
        <el-tooltip
          v-if="tooltip"
          :content="tooltip"
          placement="top"
          effect="dark"
        >
          <i class="el-icon-info kpi-sec-info-icon"></i>
        </el-tooltip>
      </div>
      <div class="kpi-sec-value-row">
        <template v-if="value === null || value === undefined">
          <span class="kpi-sec-value kpi-sec-no-data">—</span>
        </template>
        <template v-else-if="hidden">
          <span class="kpi-sec-value kpi-sec-value--hidden">****</span>
        </template>
        <template v-else>
          <span class="kpi-sec-value">
            <template v-if="prefix === '$'">$</template>
            {{ formattedValue }}
            <template v-if="prefix === '%'">%</template>
            <template v-if="prefix === 'días'"> días</template>
          </span>
          <span
            v-if="change !== null && change > 0"
            :class="['kpi-sec-change', direction]"
          >
            <i
              :class="
                direction === 'up'
                  ? 'el-icon-top'
                  : direction === 'down'
                  ? 'el-icon-bottom'
                  : 'el-icon-minus'
              "
            ></i>
            {{ change }}%
          </span>
        </template>
      </div>
      <span v-if="subtextLine" class="kpi-sec-subtext">{{ subtextLine }}</span>
      <span
        v-else-if="!hidden && hasPrevious"
        class="kpi-sec-subtext kpi-sec-previous"
      >
        Antes:
        <strong>{{ formattedPrevious }}</strong>
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: "KpiSecondaryCard",
  data() {
    return {
      hidden: localStorage.getItem("kpi-hide-values") === "true"
    };
  },
  mounted() {
    this.$root.$on("privacy-changed", this._onPrivacyChanged);
  },
  beforeDestroy() {
    this.$root.$off("privacy-changed", this._onPrivacyChanged);
  },
  props: {
    label: { type: String, default: "" },
    value: { type: [Number, null], default: 0 },
    previous: { type: Number, default: null },
    change: { type: Number, default: null },
    direction: { type: String, default: "neutral" },
    prefix: { type: String, default: "" },
    icon: { type: String, default: "el-icon-data-analysis" },
    iconColor: { type: String, default: "#3fdbbf" },
    iconBg: { type: String, default: "#e4fff4" },
    tooltip: { type: String, default: "" },
    danger: { type: Boolean, default: false },
    subtextLine: { type: String, default: "" },
    clickable: { type: Boolean, default: false }
  },
  computed: {
    formattedValue() {
      if (this.prefix === "$") {
        return new Intl.NumberFormat("es-EC", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }).format(this.value);
      }
      return this.value;
    },
    hasPrevious() {
      return (
        this.previous !== null &&
        this.previous !== undefined &&
        this.previous > 0
      );
    },
    formattedPrevious() {
      if (this.previous === null || this.previous === undefined) return "";
      if (this.prefix === "$") {
        const num = new Intl.NumberFormat("es-EC", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }).format(this.previous);
        return `$${num}`;
      }
      if (this.prefix === "%") return `${this.previous}%`;
      if (this.prefix === "días") return `${this.previous} días`;
      return this.previous;
    }
  },
  methods: {
    _onPrivacyChanged(val) {
      this.hidden = val;
    },
    onClick() {
      if (this.clickable) {
        this.$emit("click");
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.kpi-secondary {
  background: #fff;
  border: 1px solid #e4eaf6;
  border-radius: 10px;
  padding: 16px 18px;
  min-height: 100px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.kpi-sec-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  i {
    font-size: 18px;
  }
}
.kpi-sec-content {
  flex: 1;
  min-width: 0;
}
.kpi-sec-label-row {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 2px;
}
.kpi-sec-label {
  font-size: 12px;
  color: #909399;
  font-weight: 500;
}
.kpi-sec-info-icon {
  font-size: 12px;
  color: #c0c4cc;
  cursor: help;
  &:hover {
    color: #909399;
  }
}
.kpi-sec-value-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.kpi-sec-value {
  font-size: 22px;
  font-weight: 500;
  color: #31355c;
  &--hidden {
    letter-spacing: 3px;
    color: #c0c4cc;
  }
}
.kpi-sec-no-data {
  color: #c0c4cc;
  font-size: 18px;
}
.kpi-sec-change {
  font-size: 12px;
  font-weight: 600;
  &.up {
    color: #1d9e75;
  }
  &.down {
    color: #e24b4a;
  }
  &.neutral {
    color: #ba7517;
  }
}
.kpi-sec-subtext {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
  display: block;
}
.kpi-sec-previous strong {
  color: #31355c;
  font-weight: 600;
}

// Danger variant (facturas vencidas)
.kpi-secondary--danger {
  border-left: 3px solid #e24b4a;
  .kpi-sec-value {
    color: #e24b4a;
  }
}
.kpi-secondary--clickable {
  cursor: pointer;
  transition: box-shadow 0.15s;
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
}
</style>
